/**
 * ChainDetonationSkillEffect — Conjuration Swarm path upgrade passive.
 *
 * Upgrade node (detonate_upgrade_2), parent: detonate_upgrade_1 (Volatile).
 * Enemies killed by a Detonate blast produce a secondary explosion at the
 * death position:
 *   chainDamage = round(primaryDamage × chainExplosionDamageMultiplier)
 *   chainRadius  = chainExplosionRadiusTiles (default 0.75)
 *
 * The integration layer calls `onEnemyKilled(killedBySkillId, primaryDamage,
 * x, y, isChainExplosion)` when any enemy dies. This skill only reacts when:
 *   1. killedBySkillId === 'detonate'
 *   2. isChainExplosion === false (prevents infinite recursion)
 *
 * Chain explosions themselves have isChainExplosion = true in the event they
 * emit, so no secondary chains propagate.
 *
 * Also registers a trigger intent with DeathTriggerSystem on construction
 * so the integration layer can route Detonate-kill events here.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Chain Detonation
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ChainDetonationDeps {
  readonly deathTriggers: {
    /** Register intent: route 'detonate' kills to this skill via onEnemyKilled. */
    registerTrigger(params: { requireKillBySkill: string }): void;
  };
  readonly combat: {
    /** Apply secondary AoE at (x, y) within radiusTiles for chainDamage. */
    applyAoEDamage(x: number, y: number, radiusTiles: number, damage: number): void;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface ChainDetonationConfig {
  /** Fraction of primary damage dealt by the secondary explosion. */
  readonly chainExplosionDamageMultiplier: number;
  /** Blast radius of the secondary explosion in tiles. */
  readonly chainExplosionRadiusTiles: number;
}

const DEFAULT_CONFIG: ChainDetonationConfig = {
  chainExplosionDamageMultiplier: 0.5,   // spec: 50% of primary
  chainExplosionRadiusTiles: 0.75,       // spec: 0.75-tile secondary blast
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ChainDetonationSkillEffect implements SkillEffect {
  readonly skillId = 'detonate_upgrade_2';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ChainDetonationDeps;
  private readonly config: ChainDetonationConfig;

  // Observable state for scenarios
  private _chainsFired = 0;
  private _lastChainDamage = 0;
  private _lastChainX = 0;
  private _lastChainY = 0;

  constructor(deps: ChainDetonationDeps, config: Partial<ChainDetonationConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
    // Signal to integration layer: route 'detonate'-kills to onEnemyKilled.
    deps.deathTriggers.registerTrigger({ requireKillBySkill: 'detonate' });
  }

  // --- Chain Detonation-specific API ---

  /**
   * Called by integration layer when any enemy dies.
   *
   * @param killedBySkillId  Skill ID that dealt the killing blow
   * @param primaryDamage    The damage value that killed the enemy
   * @param x                Enemy death position X (world pixels)
   * @param y                Enemy death position Y (world pixels)
   * @param isChainExplosion If true, the killing hit was itself a chain —
   *                         skip to prevent infinite recursion
   */
  onEnemyKilled(
    killedBySkillId: string,
    primaryDamage: number,
    x: number,
    y: number,
    isChainExplosion = false,
  ): void {
    if (killedBySkillId !== 'detonate') return;
    if (isChainExplosion) return;

    const damageMultiplier = this.config.chainExplosionDamageMultiplier * this.deps.resolver.getMultiplicativeEffect('detonate_upgrade_2', 'chainExplosionDamageMultiplier');
    const radiusTiles = Math.max(this.config.chainExplosionRadiusTiles, this.deps.resolver.getAdditiveEffect('detonate_upgrade_2', 'chainExplosionRadiusTiles'));
    const chainDamage = Math.round(primaryDamage * damageMultiplier);
    this.deps.combat.applyAoEDamage(x, y, radiusTiles, chainDamage);

    this._chainsFired++;
    this._lastChainDamage = chainDamage;
    this._lastChainX = x;
    this._lastChainY = y;
  }

  // --- State getters ---

  get chainsFired(): number { return this._chainsFired; }
  get lastChainDamage(): number { return this._lastChainDamage; }
  get lastChainX(): number { return this._lastChainX; }
  get lastChainY(): number { return this._lastChainY; }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  get cooldown(): number { return 0; }
  update(_delta: number): void {}
  destroy(): void {}
}
